import { Flame, Trophy } from "lucide-react";
import { cn } from "@/lib/utils";

/** Playden's "Competition Ethos" notice.
 *
 *  Rendered on the Home hero, the Age Gate, the Login page, and inside the
 *  Terms of Service to set expectations up-front: Playden is a competitive,
 *  chance-based, closed-loop entertainment platform. Outcomes are final and
 *  non-refundable, rewards remain inside the Service, and no cash payouts are
 *  ever issued.
 *
 *  Component name and API preserved for import compatibility across HomePage,
 *  AgeGate, and LoginPage. Copy rewritten (Compliance Roadmap Item 9) to
 *  replace non-professional language with neutral, factual disclosures. */
export function WinnersOnlyNotice({ variant = "hero", className = "" }) {
  if (variant === "inline") {
    return (
      <div className={cn(
        "rounded-lg border border-amber-400/35 bg-amber-400/[0.06] px-3 py-2 text-[12px] leading-snug text-amber-100/90",
        className
      )} data-testid="winners-only-notice">
        <span className="font-mono uppercase tracking-[0.2em] text-amber-300/90 mr-1.5">Heads up ·</span>
        Playden is a competitive, chance-based platform. Outcomes are final and non-refundable; rewards stay inside the Service and have no cash value.{" "}
        <span className="text-white/80">Free to play — no subscription, no per-match fee.</span>
      </div>
    );
  }
  return (
    <div
      className={cn(
        "relative rounded-2xl overflow-hidden border border-amber-400/40 bg-gradient-to-br from-amber-500/[0.10] via-rose-500/[0.05] to-transparent p-4 sm:p-5",
        className
      )}
      data-testid="winners-only-notice"
    >
      <div className="absolute inset-0 holo-scan opacity-10 pointer-events-none" />
      <div className="relative flex items-start gap-3">
        <div className="h-10 w-10 rounded-xl bg-amber-400/15 border border-amber-300/40 flex items-center justify-center shrink-0">
          <Flame className="h-5 w-5 text-amber-300" />
        </div>
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-mono text-[10px] uppercase tracking-[0.35em] text-amber-300/90">Competition Ethos</span>
            <span className="inline-flex items-center gap-1 rounded-full bg-amber-300 text-black font-mono text-[9px] font-bold px-2 py-0.5">
              <Trophy className="h-3 w-3" /> Free to play
            </span>
          </div>
          <h3 className="font-display text-lg sm:text-xl text-white tracking-[0.04em] mt-1">
            Every outcome is final. Every reward stays in-Service.
          </h3>
          <p className="mt-1 text-[13px] leading-relaxed text-white/75">
            Playden is a competitive, chance-based entertainment platform. Cards can be lost in trades and matches,
            Community Prediction Pools can resolve against you, and Cavern delves can end in forfeits. Randomized
            packs, rerolls, and forges may return items of lower perceived value than the cost paid. By playing,
            you accept that these outcomes are final and non-refundable.
          </p>
          <p className="mt-2 text-[12px] text-amber-200/90">
            <span className="text-amber-300 font-semibold">Closed-loop rewards.</span> No subscription, no per-match fee.
            Credits, cards, gear, XP, and tokens stay inside Playden and have no cash value.
          </p>
        </div>
      </div>
    </div>
  );
}
